import type { ReactElement } from 'react'
import { Navigate } from 'react-router-dom'
import { useSmartImportOption } from '../../hooks/useSmartImportOption'

/**
 * #208 — la porte de `/import/intelligent`.
 *
 * L'import intelligent est une OPTION par client (décision client
 * 2026-10-06). `/import` reste l'ancien écran, pour tout le monde, comme sur
 * master. Cette route ne s'ouvre que si l'option est active pour le tenant ET
 * que l'utilisateur est administrateur (règle de l'API `/contact-import`) ;
 * sinon elle RENVOIE vers `/import` avec un message — jamais un 404 brut.
 *
 * Le motif voyage dans l'état de navigation (`location.state.smartImport`),
 * lu par `SmartImportRedirectNotice` en tête de l'ancien écran.
 */
export const SMART_IMPORT_NOTICE_STATE_KEY = 'smartImport'
export type SmartImportNoticeReason = 'disabled' | 'adminsOnly'

export function smartImportNoticeOf(state: unknown): SmartImportNoticeReason | null {
  const reason = (state as Record<string, unknown> | null | undefined)?.[SMART_IMPORT_NOTICE_STATE_KEY]
  return reason === 'disabled' || reason === 'adminsOnly' ? reason : null
}

export function SmartImportGate({ children }: { children: ReactElement }) {
  const { access } = useSmartImportOption()
  // Comme `ProtectedRoute` : rien (pas un spinner) tant qu'on ne sait pas,
  // pour ne pas faire clignoter l'écran.
  if (access === 'loading') return null
  if (access === 'enabled') return children
  const reason: SmartImportNoticeReason = access === 'not-admin' ? 'adminsOnly' : 'disabled'
  return <Navigate to="/import" replace state={{ [SMART_IMPORT_NOTICE_STATE_KEY]: reason }} />
}
